import React from 'react';
import { Spinner, WhatsAppFAB, ProductPlaceholder, formatPrice } from './catalogueHelpers';

const CatalogueView = ({
  slug,
  businessName, settings, accentColor,
  displayedProducts,
  cart, cartEnabled, cartCount, cartTotal, addToCart, removeFromCart, getCartItem,
  setBuyNowItems,
  searchQuery, setSearchQuery, searchOpen, setSearchOpen, isSearching,
  loadingMore, sentinelRef,
  setView, setSelectedProduct, setCanGoBack,
}) => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white px-4 sticky top-0 z-10">
        <div className="max-w-3xl lg:max-w-6xl mx-auto flex items-center gap-3 py-4 border-b border-gray-200">
          {searchOpen ? (
            <>
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit…"
                className="flex-1 bg-gray-100 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
              />
              <button
                onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                className="shrink-0 text-sm text-gray-500 hover:text-gray-800 font-medium transition"
              >
                Annuler
              </button>
            </>
          ) : (
            <>
              <div className="flex-1 min-w-0">
                <a href={`/c/${slug}`} className="text-lg font-bold text-gray-900 hover:no-underline">{businessName}</a>
                {settings.description && (
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed max-w-sm">{settings.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => setSearchOpen(true)}
                  className="p-2 rounded-full text-gray-600 hover:bg-gray-100 transition"
                >
                  <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" width="22" height="22">
                    <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
                  </svg>
                </button>
                {cartEnabled && (
                  <button
                    onClick={() => cartCount > 0 && setView('checkout')}
                    className="relative p-2 rounded-full text-gray-900 hover:bg-gray-100 transition"
                  >
                    <svg aria-hidden="true" fill="currentColor" height="22" viewBox="0 0 24 24" width="22" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M7.309 6.25a4.751 4.751 0 0 1 9.382 0H18.5a1.25 1.25 0 0 1 1.25 1.25v11a3.25 3.25 0 0 1-3.25 3.25h-9a3.25 3.25 0 0 1-3.25-3.25v-11A1.25 1.25 0 0 1 5.5 6.25h1.809m1.528 0a3.251 3.251 0 0 1 6.326 0H8.837M5.75 18.5V7.75h12.5V18.5a1.75 1.75 0 0 1-1.75 1.75h-9a1.75 1.75 0 0 1-1.75-1.75" clipRule="evenodd" />
                    </svg>
                    {cartCount > 0 && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1"
                        style={accentColor ? { backgroundColor: accentColor } : { backgroundColor: '#F89133' }}
                      >
                        {cartCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Products grid */}
      <div className="max-w-3xl lg:max-w-6xl mx-auto px-4 lg:px-6 py-6">
        {isSearching ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Spinner /> <span>Recherche…</span>
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {searchQuery.trim() ? 'Aucun produit trouvé.' : 'Aucun produit disponible.'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {displayedProducts.map(product => {
                const cartItem = getCartItem(product.id);
                return (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl overflow-hidden cursor-pointer transition-colors flex flex-col"
                    style={{ border: '1px solid rgb(234, 235, 236)' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgb(210, 211, 213)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgb(234, 235, 236)'}
                    onClick={() => { setSelectedProduct(product); setView('product'); setCanGoBack(true); }}
                  >
                    {product.images?.[0]?.url ? (
                      <div className="p-2">
                        <div className="aspect-[4/3] overflow-hidden rounded-xl bg-gray-100">
                          <img src={product.images[0].url} alt={product.name} className="w-full h-full object-cover" />
                        </div>
                      </div>
                    ) : (
                      <div className="p-2"><ProductPlaceholder /></div>
                    )}
                    <div className="p-3 flex flex-col min-h-[120px]">
                      <p className="text-sm font-medium text-gray-900 leading-tight line-clamp-2">{product.name}</p>
                      {product.price != null && (
                        <p className="text-sm font-medium text-gray-500 mt-1">{formatPrice(product.price)}</p>
                      )}
                      <div className="mt-auto pt-2 flex items-center">
                        {cartEnabled ? (
                          <>
                            <button
                              onClick={e => { e.stopPropagation(); removeFromCart(product); }}
                              className="flex items-center justify-center rounded-lg text-sm font-bold transition-all duration-200"
                              style={{
                                backgroundColor: 'transparent',
                                border: `1.5px solid ${accentColor || '#F89133'}`,
                                color: accentColor || '#F89133',
                                width: cartItem ? '2rem' : '0',
                                height: '2rem',
                                opacity: cartItem ? 1 : 0,
                                overflow: 'hidden',
                                pointerEvents: cartItem ? 'auto' : 'none',
                                padding: 0,
                                minWidth: 0,
                                marginRight: cartItem ? '6px' : '0',
                              }}
                            >−</button>
                            <button
                              onClick={e => { e.stopPropagation(); addToCart(product); }}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg app-btn text-white text-xs font-semibold transition active:scale-[0.97]"
                              style={accentColor ? { backgroundColor: accentColor } : {}}
                            >
                              <svg aria-hidden="true" fill="currentColor" height="12" viewBox="0 0 24 24" width="12">
                                <path fillRule="evenodd" d="M7.309 6.25a4.751 4.751 0 0 1 9.382 0H18.5a1.25 1.25 0 0 1 1.25 1.25v11a3.25 3.25 0 0 1-3.25 3.25h-9a3.25 3.25 0 0 1-3.25-3.25v-11A1.25 1.25 0 0 1 5.5 6.25h1.809m1.528 0a3.251 3.251 0 0 1 6.326 0H8.837M5.75 18.5V7.75h12.5V18.5a1.75 1.75 0 0 1-1.75 1.75h-9a1.75 1.75 0 0 1-1.75-1.75" clipRule="evenodd" />
                              </svg>
                              {cartItem ? `${cartItem.quantity} ajouté${cartItem.quantity > 1 ? 's' : ''}` : 'Ajouter'}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setBuyNowItems([{ product, quantity: 1 }]);
                              setView('checkout');
                            }}
                            className="flex-1 flex items-center justify-center py-2 rounded-lg text-xs font-semibold text-white transition active:scale-[0.97]"
                            style={{ backgroundColor: accentColor || '#111111' }}
                          >
                            Acheter maintenant
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div ref={sentinelRef} className="h-4" />
            {loadingMore && (
              <div className="flex justify-center py-6"><Spinner /></div>
            )}
          </>
        )}
      </div>

      {/* Powered by */}
      <div className="text-center py-8 text-xs text-gray-400">
        Propulsé par{' '}
        <a href="https://ouigoo.co/ouigoo-app" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-500 hover:text-gray-700 transition-colors">
          OuiGoo
        </a>
      </div>

      {settings.whatsappEnabled && settings.whatsappNumber && (
        <WhatsAppFAB
          number={settings.whatsappNumber}
          message="Bonjour, j'ai une question sur votre catalogue."
          bottomOffset={cartEnabled && cartCount > 0 ? 'bottom-24' : 'bottom-6'}
        />
      )}

      {cartEnabled && cartCount > 0 && (
        <>
          <div className="h-24" />
          <div className="fixed bottom-0 left-0 right-0 px-4 py-4 bg-white border-t border-gray-100 shadow-lg">
            <div className="max-w-3xl mx-auto">
              <button
                onClick={() => setView('checkout')}
                className="w-full app-btn text-white font-bold py-4 rounded-xl flex items-center justify-between px-5 active:scale-[0.98] transition"
                style={accentColor ? { backgroundColor: accentColor } : {}}
              >
                <span className="bg-white bg-opacity-20 rounded-lg px-2.5 py-1 text-sm font-bold">
                  {cartCount} article{cartCount > 1 ? 's' : ''}
                </span>
                <span>Commander</span>
                <span className="text-sm font-semibold opacity-90">{formatPrice(cartTotal)}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CatalogueView;
